import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useOnboarding } from './ImportJobContext';
import { IS_MOCK_API } from './onboarding.service';
import { salonService } from '../../_services/salon.service';
import siteSetting from '../../config/env';
import { removePracticeData } from './starterSetup';
import { setDemoTeam } from './demoTeam';

/**
 * Last step: go live. One screen. The booking link with copy and share,
 * two optional things for later, and the switch. Both CTAs mark onboarding
 * completed; only the primary turns online booking on.
 */
export default function GoLiveScreen({ onDone, onOpenPath }) {
  const { patchOnboarding, onboarding } = useOnboarding();
  const practice = (onboarding && onboarding.checklist && onboarding.checklist.practice) || null;
  const hasPractice = !!(practice && practice.clientId && !practice.removed && !practice.paid);
  const [tidy, setTidy] = useState(true);
  const salon = useSelector((s) => s.user && s.user.userInfo && s.user.userInfo.hairdresser && s.user.userInfo.hairdresser.salon) || {};
  const history = useHistory();
  const slug = salon.slug || salon._id || localStorage.getItem('salonId') || '';
  const link = `${siteSetting.api.weburl}book/${slug}`;
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState('');
  const canShare = typeof navigator !== 'undefined' && !!navigator.share;

  const copy = async () => {
    try { await navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch (e) { /* ignore */ }
  };
  const share = async () => {
    try { await navigator.share({ title: salon.salonName || 'Book with us', url: link }); } catch (e) { /* cancelled */ }
  };
  const go = (path) => { if (onOpenPath) onOpenPath(); history.push(path); };

  const finish = async (live) => {
    setBusy(true); setErr('');
    try {
      if (live && !IS_MOCK_API) {
        const fd = new FormData();
        fd.append('salonId', localStorage.getItem('salonId') || '');
        fd.append('onlineBookingEnabled', true);
        await salonService.updateSalon(fd);
      }
      // The example columns were scaffolding. They go when the salon goes live.
      setDemoTeam(false);
      let practicePatch = {};
      if (hasPractice && tidy) {
        await removePracticeData(practice);
        practicePatch = { practice: { ...practice, removed: true } };
      }
      await patchOnboarding({ status: 'completed', checklist: { live: live ? 'done' : 'later', ...practicePatch } });
      onDone(live ? 'live' : 'private');
    } catch (e) {
      setErr(e?.response?.data?.messages || e.message || 'Could not switch bookings on. You can do it from Settings.');
      setBusy(false);
    }
  };

  return (
    <div className="ob-screen">
      <div className="ob-eyebrow-row"><span className="ob-eyebrow">Last step</span></div>
      <h2 className="ob-h1">Take bookings live.</h2>
      <p className="ob-live-sub">Your booking page is built. Switch it on and clients can book you any time, day or night.</p>
      <div className="ob-screen-scroll">
        <div className="ob-live-link">
          <span className="ob-live-url">{link.replace(/^https?:\/\//, '')}</span>
          <div className="ob-live-link-actions">
            <button type="button" className="ob-btn ob-btn--secondary ob-btn--sm" onClick={copy}>{copied ? 'Copied' : 'Copy link'}</button>
            {canShare && <button type="button" className="ob-btn ob-btn--secondary ob-btn--sm" onClick={share}>Share</button>}
          </div>
        </div>
        <p className="ob-live-note">Put it in your Instagram bio, your Google listing and your voicemail. That is where bookings come from.</p>

        <div className="ob-receipt">
          <div className="ob-receipt-row ob-receipt-row--todo"><span className="ob-live-bullet" aria-hidden="true" /><span>Connect card payments, add your team, bring your clients in</span><button type="button" onClick={() => go('/settings/finish-setup')}>Open</button></div>
        </div>
        <p className="ob-live-note">All optional. None of it is needed to take your first booking, and it waits for you in Settings under Finish setting up.</p>
        {hasPractice && (
          <label className="ob-live-tidy">
            <input type="checkbox" checked={tidy} onChange={(e) => { const v = e.target.checked; setTidy(v); }} disabled={busy} />
            <span>
              <b>Remove the sample client and practice booking</b>
              <small>{practice.paid ? 'You checked out the practice booking, so it and the sample client stay in your history.' : 'Your services and settings stay as they are.'}</small>
            </span>
          </label>
        )}
        {err && <div className="ob-error" role="alert">{err}</div>}
      </div>
      <div className="ob-reveal-ctas">
        <button type="button" className="ob-btn ob-btn--primary" disabled={busy} onClick={() => finish(true)}>{busy ? 'One moment…' : 'Take my bookings live'}</button>
        <button type="button" className="ob-btn ob-btn--secondary" disabled={busy} onClick={() => finish(false)}>Keep it private for now</button>
      </div>
    </div>
  );
}
